import React, { useState, useEffect } from "react";

/**
 * 개선 사항
 * - /analyze-save → /analyze → (상대경로/프록시) → http://localhost:8000 → http://127.0.0.1:8000 순으로 폴백
 * - AbortController로 타임아웃(25s)
 * - 응답이 JSON이 아니거나 status!=ok일 때 친절한 에러
 * - logs 없어도 동작 (UI는 유지)
 * - ✅ 분석 결과에 요리 순서(steps) 표시 추가
 * - ✅ 채널 일괄 분석 탭 추가
 */
export default function AnalyzePanel({ apiBase = "http://localhost:8000", darkMode = false, onSaveSuccess }) {
  // === 공통 ===
  const [tab, setTab] = useState("video"); // "video" | "channel" | "recent"

  // === 영상 분석 탭 (다중 URL) ===
  const [urls, setUrls] = useState([""]); // 최대 5개
  const [analyzing, setAnalyzing] = useState(false);
  // urlState: { [idx]: { status: "idle"|"loading"|"done"|"error", results: Recipe[], error: "" } }
  const [urlState, setUrlState] = useState({});
  // recipeSaveStatus: { [`${urlIdx}_${recipeIdx}`]: ""|"saving"|"saved"|"duplicate"|"overwritten"|"error" }
  const [recipeSaveStatus, setRecipeSaveStatus] = useState({});
  // duplicateData: { [`${urlIdx}_${recipeIdx}`]: existingRecipe }
  const [duplicateData, setDuplicateData] = useState({});
  // 최근 분석 결과 — { items: [{url, results}] } 구조, localStorage로 모달 닫아도 유지
  const [recentResults, setRecentResults] = useState(() => {
    try {
      const s = localStorage.getItem("findish_recentResults");
      const p = s ? JSON.parse(s) : null;
      if (!p) return null;
      // 구형 포맷 { url, results } → 신형 { items }
      if (p.url && p.results) return { items: [{ url: p.url, results: p.results }] };
      return p;
    } catch { return null; }
  });

  useEffect(() => {
    try {
      if (recentResults) localStorage.setItem("findish_recentResults", JSON.stringify(recentResults));
    } catch {}
  }, [recentResults]);

  // === 채널 분석 탭 ===
  const [channelUrl, setChannelUrl] = useState("");
  const [maxResults, setMaxResults] = useState(5);
  const [startIndex, setStartIndex] = useState(1);
  const [chLoading, setChLoading] = useState(false);
  const [chError, setChError] = useState("");
  const [videoList, setVideoList] = useState([]);
  const [analysisStatus, setAnalysisStatus] = useState({}); // videoId → "waiting"|"analyzing"|"done"|"failed"
  const [analysisResults, setAnalysisResults] = useState({}); // videoId → result object
  const [, setExpandedId] = useState(null);
  const [batchRunning, setBatchRunning] = useState(false);
  const [saveStatus, setSaveStatus] = useState({}); // videoId → "saving"|"saved"|"duplicate"|"overwritten"|"error"|"deleted"
  const [channelDuplicateData, setChannelDuplicateData] = useState({}); // videoId → 기존 저장 레시피
  const [videoFilter, setVideoFilter] = useState("all"); // "all" | "video" | "shorts"
  const [excludePaidPromotion, setExcludePaidPromotion] = useState(true);
  const [existingUrls, setExistingUrls] = useState(new Set());
  const [editingId, setEditingId] = useState(null); // 현재 수정 중인 videoId
  const [editData, setEditData] = useState({}); // 수정 중인 데이터

  // === 중복 선택 모달 ===
  // { recipe, statusKey, existingList, onDone } | null
  const [dupModal, setDupModal] = useState(null);
  const [dupBusy, setDupBusy] = useState(false);

  const base = apiBase.replace(/\/+$/, "");

  const getVideoId = (url) => {
    const m = url.match(/(?:v=|youtu\.be\/)([A-Za-z0-9_-]{11})/);
    return m ? m[1] : null;
  };

  // 저장된 URL 목록 가져오기
  const fetchExistingUrls = async () => {
    try {
      const resp = await fetch(`${base}/existing-urls`);
      const data = await resp.json();
      if (data.ok) setExistingUrls(new Set(data.urls));
    } catch {}
  };

  // 타임아웃 가능한 fetch
  const fetchWithTimeout = async (input, init = {}, ms = 25000) => {
    const ctrl = new AbortController();
    const id = setTimeout(() => ctrl.abort(), ms);
    try {
      return await fetch(input, { ...init, signal: ctrl.signal });
    } finally {
      clearTimeout(id);
    }
  };

  // =====================
  // 영상 분석 탭 로직
  // =====================
  const addUrl = () => {
    if (urls.length < 5) setUrls((prev) => [...prev, ""]);
  };

  const removeUrl = (idx) => {
    setUrls((prev) => prev.filter((_, i) => i !== idx));
    setUrlState((prev) => {
      const next = { ...prev };
      delete next[idx];
      return next;
    });
  };

  const updateUrl = (idx, val) => {
    setUrls((prev) => prev.map((u, i) => (i === idx ? val : u)));
  };

  const runAnalysis = async () => {
    const validTargets = urls
      .map((u, i) => ({ url: u.trim(), idx: i }))
      .filter(({ url }) => url);
    if (!validTargets.length) return;

    setAnalyzing(true);
    setRecipeSaveStatus({});

    // 모두 loading 표시
    const initState = {};
    validTargets.forEach(({ idx }) => {
      initState[idx] = { status: "loading", results: [], error: "" };
    });
    setUrlState(initState);

    // 병렬 분석 — 성공 결과 누적
    const successItems = [];
    await Promise.all(
      validTargets.map(async ({ url, idx }) => {
        try {
          const resp = await fetchWithTimeout(
            `${base}/analyze`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ url }),
            },
            240000
          );
          let data;
          try {
            data = await resp.json();
          } catch {
            data = { ok: false, error: `HTTP ${resp.status} (파싱 실패)` };
          }
          if (data.ok && data.results && data.results.length > 0) {
            setUrlState((prev) => ({
              ...prev,
              [idx]: { status: "done", results: data.results, error: "" },
            }));
            successItems.push({ url, results: data.results });
          } else {
            setUrlState((prev) => ({
              ...prev,
              [idx]: { status: "error", results: [], error: data.error || "분석 실패" },
            }));
          }
        } catch (e) {
          setUrlState((prev) => ({
            ...prev,
            [idx]: {
              status: "error",
              results: [],
              error: e.name === "AbortError" ? "타임아웃 (4분 초과)" : String(e),
            },
          }));
        }
      })
    );

    if (successItems.length > 0) setRecentResults({ items: successItems });
    setAnalyzing(false);
  };

  /**
   * 통합 저장 요청 헬퍼.
   * body: { result, target_id?, save_as_new?, overwrite? }
   * duplicate 응답이 오면 { duplicate: true, existingList } 반환.
   */
  const requestSave = async (body) => {
    const resp = await fetchWithTimeout(
      `${base}/save-recipe`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      },
      10000
    );
    const data = await resp.json();
    if (data.ok && data.saved) {
      return { ok: true, saved: true, data };
    }
    if (data.reason === "duplicate") {
      return {
        ok: true, saved: false, duplicate: true,
        existingList: data.existing_list || (data.existing ? [data.existing] : []),
        existing: data.existing,
        recipe_id: data.recipe_id,
      };
    }
    return { ok: false, error: data.error || "저장 실패" };
  };

  const openDupModal = (recipe, statusKey, existingList, onDone) => {
    setDupModal({ recipe, statusKey, existingList, onDone });
  };
  const closeDupModal = () => {
    setDupModal(null);
    setDupBusy(false);
  };

  const saveVideoRecipe = async (urlIdx, recipeIdx, recipe, overwrite = false) => {
    const key = `${urlIdx}_${recipeIdx}`;
    setRecipeSaveStatus((prev) => ({ ...prev, [key]: "saving" }));
    try {
      const result = await requestSave({ result: recipe, overwrite });
      if (result.saved) {
        setRecipeSaveStatus((prev) => ({
          ...prev,
          [key]: result.data.overwritten ? "overwritten" : "saved",
        }));
        onSaveSuccess?.();
      } else if (result.duplicate) {
        setRecipeSaveStatus((prev) => ({ ...prev, [key]: "duplicate" }));
        if (result.existing) {
          setDuplicateData((prev) => ({ ...prev, [key]: result.existing }));
        }
        openDupModal(recipe, key, result.existingList, (finalStatus) => {
          setRecipeSaveStatus((prev) => ({ ...prev, [key]: finalStatus }));
          if (finalStatus === "saved" || finalStatus === "overwritten") onSaveSuccess?.();
        });
      } else {
        setRecipeSaveStatus((prev) => ({ ...prev, [key]: "error" }));
      }
    } catch {
      setRecipeSaveStatus((prev) => ({ ...prev, [key]: "error" }));
    }
  };

  // =====================
  // 채널 분석 탭 로직
  // =====================
  const fetchChannelVideos = async () => {
    setChLoading(true);
    setChError("");
    setVideoList([]);
    setAnalysisStatus({});
    setAnalysisResults({});
    setExpandedId(null);
    setSaveStatus({});
    fetchExistingUrls();

    if (!channelUrl) {
      setChError("채널 URL을 입력하세요.");
      setChLoading(false);
      return;
    }

    try {
      const resp = await fetchWithTimeout(
        `${base}/channel-videos`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ channel_url: channelUrl, max_results: maxResults, start_index: startIndex }),
        },
        15000
      );
      const data = await resp.json();
      if (data.ok) {
        setVideoList(data.videos);
        // 새 채널 프로필 썸네일을 channelData.js에 저장
        if (data.channel_title && data.channel_thumbnail) {
          fetch(`${base}/update-channel-profile`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ uploader: data.channel_title, thumbnail: data.channel_thumbnail }),
          }).catch(() => {});
        }
      } else {
        setChError(data.error || "영상 목록 가져오기 실패");
      }
    } catch (e) {
      setChError(`요청 실패: ${String(e)}`);
    } finally {
      setChLoading(false);
    }
  };

  const getFilteredList = () =>
    videoList.filter((v) => {
      if (videoFilter === "video") return v.duration > 60;
      if (videoFilter === "shorts") return v.duration <= 60;
      return true;
    }).filter((v) => !(excludePaidPromotion && v.paid_promotion));

  const startBatchAnalysis = async () => {
    const targets = getFilteredList();
    if (targets.length === 0) return;
    setBatchRunning(true);

    const initStatus = {};
    targets.forEach((v) => (initStatus[v.video_id] = "waiting"));
    setAnalysisStatus(initStatus);
    setAnalysisResults({});
    setExpandedId(null);
    setSaveStatus({});

    // 최근 분석 — 이번 배치 시작 시 초기화
    const batchItems = [];

    for (const video of targets) {
      setAnalysisStatus((prev) => ({ ...prev, [video.video_id]: "analyzing" }));

      try {
        const resp = await fetchWithTimeout(
          `${base}/analyze`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ url: video.url }),
          },
          240000
        );
        let data;
        try {
          data = await resp.json();
        } catch {
          data = { ok: false, error: `HTTP ${resp.status} (JSON 파싱 실패)` };
        }
        if (data.ok && data.results?.[0]) {
          const allResults = data.results;
          setAnalysisResults((prev) => ({ ...prev, [video.video_id]: allResults[0] }));
          setAnalysisStatus((prev) => ({ ...prev, [video.video_id]: "done" }));
          batchItems.push({ url: video.url, results: allResults });
          setRecentResults({ items: [...batchItems] });

          // ✅ 분석 성공 시 모든 결과 자동 저장 (백엔드에서 (url+name) 중복 판단)
          let savedCount = 0;
          let isDuplicate = false;
          for (const result of allResults) {
            try {
              const saveResp = await fetchWithTimeout(
                `${base}/save-recipe`,
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ result, overwrite: false }),
                },
                10000
              );
              const saveData = await saveResp.json();
              if (saveData.ok && saveData.saved) {
                savedCount++;
                setExistingUrls((prev) => new Set([...prev, result.video_url]));
                if (saveData.recipe_id) {
                  setAnalysisResults((prev) => ({
                    ...prev,
                    [video.video_id]: { ...(prev[video.video_id] || result), recipe_id: saveData.recipe_id },
                  }));
                }
              } else if (saveData.reason === "duplicate") {
                isDuplicate = true;
                if (saveData.existing) {
                  setChannelDuplicateData((prev) => ({ ...prev, [video.video_id]: saveData.existing }));
                }
                if (saveData.recipe_id) {
                  setAnalysisResults((prev) => ({
                    ...prev,
                    [video.video_id]: { ...(prev[video.video_id] || result), recipe_id: saveData.recipe_id },
                  }));
                }
              }
            } catch {
              // 자동 저장 실패 시 무시 (수동 저장 가능)
            }
          }
          if (savedCount > 0) {
            setSaveStatus((prev) => ({ ...prev, [video.video_id]: "saved" }));
          } else if (isDuplicate || existingUrls.has(allResults[0].video_url)) {
            setSaveStatus((prev) => ({ ...prev, [video.video_id]: "duplicate" }));
          }
        } else {
          setAnalysisResults((prev) => ({
            ...prev,
            [video.video_id]: { error: data.error || `HTTP ${resp.status} 분석 실패` },
          }));
          setAnalysisStatus((prev) => ({ ...prev, [video.video_id]: "failed" }));
        }
      } catch (e) {
        setAnalysisResults((prev) => ({
          ...prev,
          [video.video_id]: { error: e.name === "AbortError" ? "타임아웃 (2분 초과)" : String(e) },
        }));
        setAnalysisStatus((prev) => ({ ...prev, [video.video_id]: "failed" }));
      }
    }

    setBatchRunning(false);
  };

  const saveRecipe = async (videoId, overwrite = false) => {
    const r = analysisResults[videoId];
    if (!r || r.error) return;

    setSaveStatus((prev) => ({ ...prev, [videoId]: "saving" }));

    try {
      const result = await requestSave({ result: r, overwrite });
      if (result.saved) {
        setSaveStatus((prev) => ({ ...prev, [videoId]: result.data.overwritten ? "overwritten" : "saved" }));
        setExistingUrls((prev) => new Set([...prev, r.video_url]));
        if (result.data.recipe_id) {
          setAnalysisResults((prev) => ({ ...prev, [videoId]: { ...r, recipe_id: result.data.recipe_id } }));
        }
        onSaveSuccess?.();
      } else if (result.duplicate) {
        setSaveStatus((prev) => ({ ...prev, [videoId]: "duplicate" }));
        openDupModal(r, `ch_${videoId}`, result.existingList, (finalStatus) => {
          setSaveStatus((prev) => ({ ...prev, [videoId]: finalStatus }));
          if (finalStatus === "saved" || finalStatus === "overwritten") {
            setExistingUrls((prev) => new Set([...prev, r.video_url]));
            onSaveSuccess?.();
          }
        });
      } else {
        setSaveStatus((prev) => ({ ...prev, [videoId]: "error" }));
      }
    } catch {
      setSaveStatus((prev) => ({ ...prev, [videoId]: "error" }));
    }
  };

  // ✅ 저장 취소(삭제)
  const deleteRecipe = async (videoId) => {
    const r = analysisResults[videoId];
    if (!r || !r.video_url) return;

    try {
      const resp = await fetchWithTimeout(
        `${base}/delete-recipe`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ recipe_id: r.recipe_id, video_url: r.video_url }),
        },
        10000
      );
      const data = await resp.json();
      if (data.ok && data.deleted) {
        setSaveStatus((prev) => ({ ...prev, [videoId]: "deleted" }));
        setExistingUrls((prev) => {
          const next = new Set(prev);
          next.delete(r.video_url);
          return next;
        });
      }
    } catch {
      // 삭제 실패 시 무시
    }
  };

  // ✅ 단일 영상 재분석
  const reanalyzeVideo = async (video) => {
    setAnalysisStatus((prev) => ({ ...prev, [video.video_id]: "analyzing" }));
    setSaveStatus((prev) => ({ ...prev, [video.video_id]: undefined }));

    try {
      const resp = await fetchWithTimeout(
        `${base}/analyze`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ url: video.url }),
        },
        240000
      );
      let data;
      try { data = await resp.json(); } catch { data = { ok: false, error: `HTTP ${resp.status}` }; }

      if (data.ok && data.results?.[0]) {
        const firstResult = data.results[0];
        setAnalysisResults((prev) => ({ ...prev, [video.video_id]: firstResult }));
        setAnalysisStatus((prev) => ({ ...prev, [video.video_id]: "done" }));
        if (!existingUrls.has(firstResult.video_url)) {
          try {
            const saveResp = await fetchWithTimeout(`${base}/save-recipe`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ result: firstResult, overwrite: false }),
            }, 10000);
            const saveData = await saveResp.json();
            if (saveData.ok && saveData.saved) {
              setSaveStatus((prev) => ({ ...prev, [video.video_id]: "saved" }));
              setExistingUrls((prev) => new Set([...prev, firstResult.video_url]));
              if (saveData.recipe_id) setAnalysisResults((prev) => ({ ...prev, [video.video_id]: { ...firstResult, recipe_id: saveData.recipe_id } }));
            } else if (saveData.reason === "duplicate") {
              setSaveStatus((prev) => ({ ...prev, [video.video_id]: "duplicate" }));
              if (saveData.existing) setChannelDuplicateData((prev) => ({ ...prev, [video.video_id]: saveData.existing }));
              if (saveData.recipe_id) setAnalysisResults((prev) => ({ ...prev, [video.video_id]: { ...firstResult, recipe_id: saveData.recipe_id } }));
            }
          } catch {}
        } else {
          setSaveStatus((prev) => ({ ...prev, [video.video_id]: "duplicate" }));
        }
      } else {
        setAnalysisResults((prev) => ({ ...prev, [video.video_id]: { error: data.error || "분석 실패" } }));
        setAnalysisStatus((prev) => ({ ...prev, [video.video_id]: "failed" }));
      }
    } catch (e) {
      setAnalysisResults((prev) => ({ ...prev, [video.video_id]: { error: e.name === "AbortError" ? "타임아웃" : String(e) } }));
      setAnalysisStatus((prev) => ({ ...prev, [video.video_id]: "failed" }));
    }
  };

  // ✅ URL로 직접 삭제 (분석 결과 없이 기존 저장된 레시피 삭제)
  const deleteRecipeByUrl = async (videoId, videoUrl) => {
    try {
      const resp = await fetchWithTimeout(`${base}/delete-recipe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ video_url: videoUrl }),
      }, 10000);
      const data = await resp.json();
      if (data.ok && data.deleted) {
        setSaveStatus((prev) => ({ ...prev, [videoId]: "deleted" }));
        setExistingUrls((prev) => { const next = new Set(prev); next.delete(videoUrl); return next; });
      }
    } catch {}
  };

  // ✅ 수정 시작
  const startEdit = (videoId) => {
    const r = analysisResults[videoId];
    if (!r) return;
    setEditingId(videoId);
    setEditData({
      name: r.name || "",
      ingredients: (r.ingredients || []).join(", "),
      steps: (r.steps || []).join("\n"),
    });
  };

  // ✅ 수정 저장
  const saveEdit = async (videoId) => {
    const r = analysisResults[videoId];
    if (!r) return;

    const updated = {
      ...r,
      name: editData.name.trim(),
      ingredients: editData.ingredients.split(",").map((s) => s.trim()).filter(Boolean),
      steps: editData.steps.split("\n").map((s) => s.trim()).filter(Boolean),
    };

    setAnalysisResults((prev) => ({ ...prev, [videoId]: updated }));
    setEditingId(null);

    // 이미 저장된 상태면 덮어쓰기로 재저장
    if (saveStatus[videoId] === "saved" || saveStatus[videoId] === "overwritten" || existingUrls.has(r.video_url)) {
      setSaveStatus((prev) => ({ ...prev, [videoId]: "saving" }));
      try {
        const resp = await fetchWithTimeout(
          `${base}/save-recipe`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ result: updated, overwrite: true }),
          },
          10000
        );
        const data = await resp.json();
        if (data.ok && data.saved) {
          setSaveStatus((prev) => ({ ...prev, [videoId]: "saved" }));
        }
      } catch {
        setSaveStatus((prev) => ({ ...prev, [videoId]: "error" }));
      }
    }
  };

  const formatDuration = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const statusIcon = (s) => ({ waiting: "·", analyzing: "···", done: "✓", failed: "–" }[s] || "–");

  // =====================
  // 탭 스타일
  // =====================
  const tabStyle = (active) => ({
    padding: "8px 20px",
    cursor: "pointer",
    fontWeight: active ? 700 : 400,
    borderBottom: active ? `2px solid ${darkMode ? "#fff" : "#000"}` : "2px solid transparent",
    color: active ? (darkMode ? "#fff" : "#000") : darkMode ? "#888" : "#999",
    background: "none",
    border: "none",
    borderBottomWidth: 2,
    borderBottomStyle: "solid",
    borderBottomColor: active ? (darkMode ? "#fff" : "#000") : "transparent",
    fontSize: 15,
  });

  return (
    <section className="container" style={{ marginTop: 24 }}>
      <h2 style={{ fontSize: 18, marginBottom: 12 }}>🔎 유튜브 링크 분석</h2>

      {/* 탭 */}
      <div style={{ display: "flex", gap: 0, marginBottom: 16, borderBottom: `1px solid ${darkMode ? "#333" : "#eee"}` }}>
        <button style={tabStyle(tab === "video")} onClick={() => setTab("video")}>
          영상 분석
        </button>
        <button style={tabStyle(tab === "channel")} onClick={() => setTab("channel")}>
          채널 분석
        </button>
        <button style={tabStyle(tab === "recent")} onClick={() => setTab("recent")}>
          최근 분석{recentResults ? " ✓" : ""}
        </button>
      </div>

      {/* ===== 영상 분석 탭 ===== */}
      {tab === "video" && (
        <>
          {/* URL 입력 목록 */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {urls.map((u, idx) => {
              const vid = getVideoId(u);
              return (
                <div key={idx} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  {/* 썸네일 미리보기 */}
                  {vid ? (
                    <a href={u} target="_blank" rel="noreferrer" style={{ flexShrink: 0 }}>
                      <img
                        src={`https://img.youtube.com/vi/${vid}/mqdefault.jpg`}
                        alt=""
                        style={{ width: 80, height: 45, borderRadius: 6, objectFit: "cover" }}
                      />
                    </a>
                  ) : (
                    <div
                      style={{
                        width: 80, height: 45, borderRadius: 6, flexShrink: 0,
                        background: darkMode ? "#222" : "#eee",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 18, color: darkMode ? "#555" : "#bbb",
                      }}
                    >▶</div>
                  )}
                  <input
                    value={u}
                    onChange={(e) => updateUrl(idx, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !analyzing && urls.some((x) => x.trim())) runAnalysis();
                    }}
                    placeholder={`YouTube URL ${idx + 1}`}
                    style={{
                      flex: 1,
                      border: "1px solid #ddd",
                      borderRadius: 8,
                      padding: "8px 12px",
                      background: darkMode ? "#111" : "#fff",
                      color: darkMode ? "#eee" : "#222",
                    }}
                  />
                  {urls.length > 1 && (
                    <button
                      onClick={() => removeUrl(idx)}
                      style={{
                        background: "none", border: "none", cursor: "pointer",
                        color: darkMode ? "#888" : "#aaa", fontSize: 18, padding: "0 4px",
                      }}
                      title="제거"
                    >×</button>
                  )}
                </div>
              );
            })}
          </div>

          {/* URL 추가 + 분석 버튼 */}
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            {urls.length < 5 && (
              <button
                onClick={addUrl}
                style={{
                  background: "none",
                  border: `1px dashed ${darkMode ? "#555" : "#ccc"}`,
                  borderRadius: 8,
                  padding: "6px 14px",
                  fontSize: 13,
                  color: darkMode ? "#aaa" : "#888",
                  cursor: "pointer",
                }}
              >
                + URL 추가 ({urls.length}/5)
              </button>
            )}
            <button
              onClick={runAnalysis}
              disabled={analyzing || !urls.some((u) => u.trim())}
              style={{
                background: darkMode ? "#fff" : "#000",
                color: darkMode ? "#000" : "#fff",
                borderRadius: 8,
                padding: "8px 18px",
                fontWeight: 600,
                opacity: analyzing || !urls.some((u) => u.trim()) ? 0.6 : 1,
                cursor: "pointer",
                border: "none",
              }}
            >
              {analyzing ? "분석 중..." : "분석 실행"}
            </button>
          </div>

          {/* 분석 결과 — URL별 */}
          {urls.map((u, urlIdx) => {
            const state = urlState[urlIdx];
            if (!state) return null;
            const vid = getVideoId(u);

            return (
              <div
                key={urlIdx}
                style={{
                  marginTop: 14,
                  border: `1px solid ${darkMode ? "#333" : "#e5e7eb"}`,
                  borderRadius: 10,
                  overflow: "hidden",
                }}
              >
                {/* URL 헤더 */}
                <div
                  style={{
                    background: darkMode ? "#1a1a1a" : "#f8faff",
                    padding: "8px 14px",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    fontSize: 13,
                    borderBottom: `1px solid ${darkMode ? "#333" : "#e5e7eb"}`,
                  }}
                >
                  {state.status === "loading" && <span style={{ color: darkMode ? "#888" : "#666", fontSize: 13 }}>분석 중...</span>}
                  {state.status === "done" && <span style={{ color: darkMode ? "#aaa" : "#555", fontSize: 14 }}>✓</span>}
                  {state.status === "error" && <span style={{ color: darkMode ? "#777" : "#999", fontSize: 14 }}>–</span>}
                  {vid ? (
                    <a href={u} target="_blank" rel="noreferrer" style={{ color: darkMode ? "#93b5ff" : "#1d4ed8" }}>
                      {u}
                    </a>
                  ) : (
                    <span style={{ color: darkMode ? "#888" : "#999" }}>{u}</span>
                  )}
                  {state.status === "done" && state.results.length > 1 && (
                    <span style={{
                      background: "#fef3c7", color: "#b45309",
                      borderRadius: 99, padding: "1px 8px", fontSize: 12, fontWeight: 600,
                    }}>
                      요리 {state.results.length}개
                    </span>
                  )}
                </div>

                {/* 오류 */}
                {state.status === "error" && (
                  <div style={{
                    padding: "10px 14px",
                    color: darkMode ? "#888" : "#666",
                    fontSize: 13,
                    borderLeft: `2px solid ${darkMode ? "#444" : "#ddd"}`,
                  }}>
                    {state.error}
                  </div>
                )}

                {/* 요리 카드 목록 */}
                {state.status === "done" &&
                  state.results.map((recipe, recipeIdx) => {
                    const key = `${urlIdx}_${recipeIdx}`;
                    const sv = recipeSaveStatus[key] || "";
                    return (
                      <div
                        key={recipeIdx}
                        style={{
                          padding: "12px 14px",
                          borderTop: recipeIdx > 0 ? `1px solid ${darkMode ? "#2a2a2a" : "#f0f0f0"}` : undefined,
                          background: darkMode ? "#0b0b0b" : "#fff",
                          color: darkMode ? "#e6e6e6" : "#222",
                        }}
                      >
                        {state.results.length > 1 && (
                          <div style={{
                            fontSize: 11, fontWeight: 700, color: darkMode ? "#888" : "#aaa",
                            textTransform: "uppercase", letterSpacing: 1, marginBottom: 6,
                          }}>
                            요리 {recipeIdx + 1}
                          </div>
                        )}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: 15 }}>{recipe.name}</div>
                            <div style={{ fontSize: 12, color: darkMode ? "#888" : "#999", marginTop: 2 }}>
                              {recipe.uploader} · {recipe.upload_date} · 출처: {recipe.source}
                            </div>
                          </div>
                          {/* 저장 버튼 */}
                          <div style={{ flexShrink: 0, marginLeft: 12 }}>
                            {sv === "saved" || sv === "overwritten" ? (
                              <span style={{ color: darkMode ? "#aaa" : "#666", fontWeight: 500, fontSize: 13 }}>
                                {sv === "overwritten" ? "갱신됨 ✓" : "저장됨 ✓"}
                              </span>
                            ) : sv === "duplicate" ? (
                              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                                <span style={{ color: darkMode ? "#888" : "#999", fontSize: 12 }}>이미 저장됨</span>
                                <button
                                  onClick={() => saveVideoRecipe(urlIdx, recipeIdx, recipe, false)}
                                  style={{
                                    background: "none", color: darkMode ? "#aaa" : "#666",
                                    border: `1px solid ${darkMode ? "#444" : "#ccc"}`, borderRadius: 6,
                                    padding: "2px 8px", fontSize: 12, cursor: "pointer",
                                  }}
                                >덮어쓰기</button>
                              </div>
                            ) : sv === "error" ? (
                              <span style={{ color: darkMode ? "#888" : "#999", fontSize: 13 }}>저장 실패</span>
                            ) : (
                              <button
                                onClick={() => saveVideoRecipe(urlIdx, recipeIdx, recipe)}
                                disabled={sv === "saving"}
                                style={{
                                  background: darkMode ? "#fff" : "#000", color: darkMode ? "#000" : "#fff",
                                  border: "none", borderRadius: 8,
                                  padding: "6px 16px", fontSize: 13, fontWeight: 600,
                                  cursor: "pointer", opacity: sv === "saving" ? 0.5 : 1,
                                }}
                              >
                                {sv === "saving" ? "저장 중..." : "저장"}
                              </button>
                            )}
                          </div>
                        </div>

                        {/* 덮어쓰기 비교 패널 — 기존 vs 새 데이터 나란히 */}
                        {sv === "duplicate" && (() => {
                          const ex = duplicateData[`${urlIdx}_${recipeIdx}`];
                          const exIngredients = ex ? (ex["재료"] || ex.ingredients || []) : [];
                          const exSteps = ex ? (ex["순서"] || ex.steps || []) : [];
                          const cardBase = {
                            borderRadius: 8, padding: "10px 12px", fontSize: 12,
                            background: darkMode ? "#1a1a1a" : "#f9f9f9",
                            border: `1px solid ${darkMode ? "#2a2a2a" : "#e5e7eb"}`,
                            color: darkMode ? "#d4d4d4" : "#333",
                          };
                          return (
                            <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: ex ? "1fr 1fr" : "1fr", gap: 10 }}>
                              {ex && (
                                <div style={{ ...cardBase, borderLeft: "3px solid #f59e0b" }}>
                                  <div style={{ fontWeight: 700, fontSize: 11, marginBottom: 6, color: "#f59e0b" }}>
                                    💾 기존 저장 데이터
                                  </div>
                                  <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>{ex["메뉴"] || ex.name}</div>
                                  <div style={{ marginBottom: 3 }}>
                                    <b>재료 ({exIngredients.length}):</b>{" "}
                                    {exIngredients.slice(0, 6).join(", ")}{exIngredients.length > 6 ? "..." : ""}
                                  </div>
                                  <div><b>순서:</b> {exSteps.length}단계</div>
                                  {exSteps.length > 0 && (
                                    <ol style={{ marginLeft: 14, marginTop: 4 }}>
                                      {exSteps.slice(0, 3).map((s, i) => <li key={i} style={{ marginBottom: 2 }}>{s}</li>)}
                                      {exSteps.length > 3 && <li style={{ listStyle: "none", opacity: 0.6 }}>... 외 {exSteps.length - 3}단계</li>}
                                    </ol>
                                  )}
                                </div>
                              )}
                              <div style={{ ...cardBase, borderLeft: "3px solid #22c55e" }}>
                                <div style={{ fontWeight: 700, fontSize: 11, marginBottom: 6, color: "#22c55e" }}>
                                  🆕 새로 가져온 데이터
                                </div>
                                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>{recipe.name}</div>
                                <div style={{ marginBottom: 3 }}>
                                  <b>재료 ({(recipe.ingredients || []).length}):</b>{" "}
                                  {(recipe.ingredients || []).slice(0, 6).join(", ")}{(recipe.ingredients || []).length > 6 ? "..." : ""}
                                </div>
                                <div><b>순서:</b> {(recipe.steps || []).length}단계</div>
                                {recipe.steps && recipe.steps.length > 0 && (
                                  <ol style={{ marginLeft: 14, marginTop: 4 }}>
                                    {recipe.steps.slice(0, 3).map((s, i) => <li key={i} style={{ marginBottom: 2 }}>{s}</li>)}
                                    {recipe.steps.length > 3 && <li style={{ listStyle: "none", opacity: 0.6 }}>... 외 {recipe.steps.length - 3}단계</li>}
                                  </ol>
                                )}
                              </div>
                            </div>
                          );
                        })()}

                        {/* 태그 뱃지 */}
                        {(recipe.servings || (recipe.tags || []).length > 0) && (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 8 }}>
                            {recipe.servings && (
                              <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, background: "#fff3e0", color: "#e07b00", border: "1px solid #fcd9a0", fontWeight: 500 }}>
                                {recipe.servings}
                              </span>
                            )}
                            {(recipe.tags || []).filter(t => t !== recipe.servings).map((tag, i) => (
                              <span key={i} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, background: darkMode ? "#2a2a2a" : "#f0f0f0", color: darkMode ? "#bbb" : "#555", border: `1px solid ${darkMode ? "#3a3a3a" : "#e0e0e0"}` }}>
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Validator 결과 */}
                        {recipe.validation_result && (
                          <div style={{ fontSize: 11, marginTop: 6, marginBottom: 4, padding: "3px 8px", borderRadius: 6, background: recipe.validation_result.flagged?.length > 0 ? (darkMode ? "#2a1f00" : "#fffbe6") : (darkMode ? "#0d1f0d" : "#f0faf0"), color: recipe.validation_result.flagged?.length > 0 ? "#b07800" : "#2e7d32", border: `1px solid ${recipe.validation_result.flagged?.length > 0 ? "#fcd9a0" : "#a5d6a7"}` }}>
                            {recipe.validation_result.summary}
                            {recipe.validation_result.fixed?.length > 0 && (
                              <span style={{ marginLeft: 6, opacity: 0.8 }}>
                                ({recipe.validation_result.fixed.map(f => `${f.ingredient}: ${f.original}→${f.fixed}`).join(", ")})
                              </span>
                            )}
                          </div>
                        )}

                        {/* 재료 + 용량 */}
                        <div style={{ marginTop: 8, fontSize: 13 }}>
                          <b>재료 ({(recipe.ingredients || []).length}):</b>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
                            {(recipe.ingredients || []).map((ing, i) => {
                              const measured = (recipe.ingredients_measured || [])[i] || "";
                              const amount = measured.startsWith(ing) ? measured.slice(ing.length).trim() : measured;
                              return (
                                <span key={i} style={{ fontSize: 12, padding: "2px 8px", borderRadius: 99, background: darkMode ? "#1e1e1e" : "#f5f5f5", border: `1px solid ${darkMode ? "#333" : "#e5e7eb"}`, color: darkMode ? "#ddd" : "#333" }}>
                                  {ing}{amount && <span style={{ opacity: 0.6, marginLeft: 4 }}>{amount}</span>}
                                </span>
                              );
                            })}
                          </div>
                        </div>

                        {recipe.steps && recipe.steps.length > 0 && (
                          <div style={{ marginTop: 8, fontSize: 13 }}>
                            <b>요리 순서:</b>
                            <ol style={{ marginLeft: 18, marginTop: 4 }}>
                              {recipe.steps.map((s, i) => (
                                <li key={i} style={{ marginBottom: 2 }}>{s}</li>
                              ))}
                            </ol>
                          </div>
                        )}

                        {/* TIP */}
                        {(recipe.tips || []).length > 0 && (
                          <div style={{ marginTop: 8, fontSize: 13 }}>
                            <b>💡 TIP</b>
                            <ul style={{ marginLeft: 16, marginTop: 4 }}>
                              {recipe.tips.map((t, i) => (
                                <li key={i} style={{ marginBottom: 2, color: darkMode ? "#bbb" : "#555" }}>{t}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            );
          })}
        </>
      )}

      {/* ===== 채널 분석 탭 ===== */}
      {tab === "channel" && (
        <>
          {/* 입력 영역 */}
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <input
              value={channelUrl}
              onChange={(e) => setChannelUrl(e.target.value)}
              placeholder="채널 URL (https://www.youtube.com/@handle)"
              style={{
                flex: 1,
                minWidth: 250,
                border: "1px solid #ddd",
                borderRadius: 8,
                padding: "8px 12px",
                background: darkMode ? "#111" : "#fff",
                color: darkMode ? "#eee" : "#222",
              }}
            />
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <label style={{ fontSize: 13, color: darkMode ? "#bbb" : "#555" }}>시작:</label>
              <input
                type="number"
                min={1}
                max={500}
                value={startIndex}
                onChange={(e) => setStartIndex(Math.min(500, Math.max(1, Number(e.target.value) || 1)))}
                style={{
                  width: 56,
                  border: "1px solid #ddd",
                  borderRadius: 6,
                  padding: "6px 8px",
                  textAlign: "center",
                  background: darkMode ? "#111" : "#fff",
                  color: darkMode ? "#eee" : "#222",
                }}
              />
              <label style={{ fontSize: 13, color: darkMode ? "#bbb" : "#555" }}>개수:</label>
              <input
                type="number"
                min={1}
                max={500}
                value={maxResults}
                onChange={(e) => setMaxResults(Math.min(500, Math.max(1, Number(e.target.value) || 1)))}
                style={{
                  width: 56,
                  border: "1px solid #ddd",
                  borderRadius: 6,
                  padding: "6px 8px",
                  textAlign: "center",
                  background: darkMode ? "#111" : "#fff",
                  color: darkMode ? "#eee" : "#222",
                }}
              />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
              {[
                { key: "all", label: "전체" },
                { key: "video", label: "동영상" },
                { key: "shorts", label: "Shorts" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setVideoFilter(opt.key)}
                  style={{
                    padding: "6px 10px",
                    fontSize: 13,
                    border: `1px solid ${darkMode ? "#444" : "#ddd"}`,
                    borderRadius: 6,
                    cursor: "pointer",
                    background:
                      videoFilter === opt.key
                        ? darkMode ? "#fff" : "#000"
                        : darkMode ? "#111" : "#fff",
                    color:
                      videoFilter === opt.key
                        ? darkMode ? "#000" : "#fff"
                        : darkMode ? "#ccc" : "#555",
                    fontWeight: videoFilter === opt.key ? 600 : 400,
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <button
              onClick={fetchChannelVideos}
              disabled={chLoading || !channelUrl}
              style={{
                background: darkMode ? "#fff" : "#000",
                color: darkMode ? "#000" : "#fff",
                borderRadius: 8,
                padding: "8px 14px",
                opacity: chLoading || !channelUrl ? 0.6 : 1,
                whiteSpace: "nowrap",
              }}
            >
              {chLoading ? "가져오는 중..." : "영상 목록 가져오기"}
            </button>
          </div>

          {chError && (
            <div
              style={{
                marginTop: 8,
                color: darkMode ? "#888" : "#666",
                borderLeft: `2px solid ${darkMode ? "#444" : "#ddd"}`,
                paddingLeft: 10,
                fontSize: 13,
              }}
            >
              {chError}
            </div>
          )}

          {/* 영상 목록 테이블 */}
          {videoList.length > 0 && (() => {
            const filteredList = videoList.filter((v) => {
              if (videoFilter === "video") return v.duration > 60;
              if (videoFilter === "shorts") return v.duration <= 60;
              return true;
            }).filter((v) => !(excludePaidPromotion && v.paid_promotion));
            return (
            <div style={{ marginTop: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontWeight: 600, fontSize: 15 }}>
                    영상 목록 ({filteredList.length}개{videoFilter !== "all" || excludePaidPromotion ? ` / 전체 ${videoList.length}개` : ""})
                  </span>
                  <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13, cursor: "pointer", color: darkMode ? "#bbb" : "#555" }}>
                    <input
                      type="checkbox"
                      checked={excludePaidPromotion}
                      onChange={(e) => setExcludePaidPromotion(e.target.checked)}
                    />
                    광고 영상 제외
                    {videoList.filter(v => v.paid_promotion).length > 0 && (
                      <span style={{ color: "#f59e0b", fontSize: 12 }}>
                        ({videoList.filter(v => v.paid_promotion).length}개)
                      </span>
                    )}
                  </label>
                </div>
                <button
                  onClick={startBatchAnalysis}
                  disabled={batchRunning}
                  style={{
                    background: darkMode ? "#fff" : "#000",
                    color: darkMode ? "#000" : "#fff",
                    borderRadius: 8,
                    padding: "6px 14px",
                    fontSize: 14,
                    opacity: batchRunning ? 0.5 : 1,
                    border: "none",
                    cursor: batchRunning ? "not-allowed" : "pointer",
                  }}
                >
                  {batchRunning ? "분석 진행 중..." : "전체 분석 시작"}
                </button>
              </div>

              <div
                style={{
                  border: `1px solid ${darkMode ? "#333" : "#eee"}`,
                  borderRadius: 8,
                  overflow: "hidden",
                }}
              >
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, tableLayout: "fixed" }}>
                  <thead>
                    <tr
                      style={{
                        background: darkMode ? "#1a1a1a" : "#f5f5f5",
                        textAlign: "left",
                      }}
                    >
                      <th style={{ padding: "8px 12px", width: 36 }}>#</th>
                      <th style={{ padding: "8px 12px" }}>제목</th>
                      <th style={{ padding: "8px 12px", width: 70, textAlign: "center" }}>유형</th>
                      <th style={{ padding: "8px 12px", width: 100 }}>날짜</th>
                      <th style={{ padding: "8px 12px", width: 60 }}>길이</th>
                      <th style={{ padding: "8px 12px", width: 60, textAlign: "center" }}>상태</th>
                      <th style={{ padding: "8px 12px", width: 120, textAlign: "center" }}>저장</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredList.map((v, i) => {
                      const st = analysisStatus[v.video_id];
                      const res = analysisResults[v.video_id];
                      const sv = saveStatus[v.video_id];

                      return (
                        <React.Fragment key={v.video_id}>
                          <tr
                            style={{
                              borderBottom: (st === "done" || st === "failed") && res
                                ? "none"
                                : `1px solid ${darkMode ? "#222" : "#eee"}`,
                            }}
                          >
                            <td style={{ padding: "8px 12px" }}>{i + 1}</td>
                            <td style={{ padding: "8px 12px" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <a href={v.url} target="_blank" rel="noreferrer" style={{ flexShrink: 0 }}>
                                  <img
                                    src={`https://img.youtube.com/vi/${v.video_id}/mqdefault.jpg`}
                                    alt=""
                                    style={{
                                      width: 120,
                                      height: 68,
                                      borderRadius: 6,
                                      objectFit: "cover",
                                    }}
                                  />
                                </a>
                                <div style={{ minWidth: 0 }}>
                                  {v.paid_promotion && (
                                    <span style={{
                                      display: "inline-block",
                                      fontSize: 11,
                                      fontWeight: 600,
                                      color: "#fff",
                                      background: "#f59e0b",
                                      borderRadius: 4,
                                      padding: "1px 5px",
                                      marginBottom: 3,
                                    }}>유료광고</span>
                                  )}
                                  <a
                                    href={v.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    title={v.title}
                                    style={{
                                      color: darkMode ? "#93b5ff" : "#1d4ed8",
                                      textDecoration: "none",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      display: "block",
                                    }}
                                  >
                                    {v.title}
                                  </a>
                                </div>
                              </div>
                            </td>
                            <td style={{ padding: "8px 12px", fontSize: 12, textAlign: "center" }}>
                              {v.duration <= 60 ? (
                                <span style={{
                                  background: darkMode ? "#2a2a2a" : "#f0f0f0",
                                  color: darkMode ? "#aaa" : "#666",
                                  borderRadius: 4,
                                  padding: "2px 6px",
                                  fontSize: 11,
                                  fontWeight: 500,
                                }}>Shorts</span>
                              ) : (
                                <span style={{
                                  background: darkMode ? "#333" : "#e5e7eb",
                                  color: darkMode ? "#ccc" : "#555",
                                  borderRadius: 4,
                                  padding: "2px 6px",
                                }}>동영상</span>
                              )}
                            </td>
                            <td style={{ padding: "8px 12px", fontSize: 13, whiteSpace: "nowrap" }}>
                              {v.published_at ? v.published_at.slice(0, 10) : "-"}
                            </td>
                            <td style={{ padding: "8px 12px", fontSize: 13 }}>
                              {formatDuration(v.duration)}
                            </td>
                            <td style={{ padding: "8px 12px", textAlign: "center" }}>
                              {st ? statusIcon(st) : "-"}
                            </td>
                            <td
                              style={{ padding: "8px 12px", textAlign: "center" }}
                            >
                              {st === "failed" ? (
                                <button
                                  onClick={() => reanalyzeVideo(v)}
                                  style={{
                                    background: "none", color: darkMode ? "#aaa" : "#555",
                                    border: `1px solid ${darkMode ? "#444" : "#ccc"}`, borderRadius: 99,
                                    padding: "3px 10px", fontSize: 11, cursor: "pointer",
                                  }}
                                >다시 분석</button>
                              ) : st === "done" && res && !res.error ? (
                                sv === "deleted" ? (
                                  <span style={{
                                    background: darkMode ? "#2a2a2a" : "#f3f4f6", color: darkMode ? "#666" : "#999",
                                    borderRadius: 99, padding: "3px 10px", fontSize: 11, fontWeight: 600,
                                  }}>삭제됨</span>
                                ) : sv === "saved" || sv === "overwritten" ? (
                                  <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                                    <span style={{
                                      background: darkMode ? "#2a2a2a" : "#f3f4f6",
                                      color: darkMode ? "#aaa" : "#666",
                                      borderRadius: 99, padding: "3px 10px", fontSize: 11, fontWeight: 500,
                                    }}>{sv === "overwritten" ? "갱신됨" : "저장됨"}</span>
                                    <button
                                      onClick={() => deleteRecipe(v.video_id)}
                                      style={{
                                        background: "none", color: darkMode ? "#666" : "#999",
                                        border: `1px solid ${darkMode ? "#444" : "#ddd"}`, borderRadius: 99,
                                        padding: "3px 8px", fontSize: 11, cursor: "pointer",
                                      }}
                                    >삭제</button>
                                  </div>
                                ) : sv === "duplicate" ? (
                                  <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                                    <button
                                      onClick={() => saveRecipe(v.video_id, false)}
                                      style={{
                                        background: "none", color: darkMode ? "#aaa" : "#555",
                                        border: `1px solid ${darkMode ? "#444" : "#ccc"}`, borderRadius: 99,
                                        padding: "3px 10px", fontSize: 11, cursor: "pointer",
                                      }}
                                      title="클릭하면 새 데이터로 덮어씁니다"
                                    >덮어쓰기</button>
                                    <button
                                      onClick={() => deleteRecipeByUrl(v.video_id, v.url)}
                                      style={{
                                        background: "none", color: darkMode ? "#666" : "#999",
                                        border: `1px solid ${darkMode ? "#444" : "#ddd"}`, borderRadius: 99,
                                        padding: "3px 8px", fontSize: 11, cursor: "pointer",
                                      }}
                                    >삭제</button>
                                  </div>
                                ) : sv === "error" ? (
                                  <span style={{
                                    background: "#fef2f2", color: "#dc2626",
                                    borderRadius: 99, padding: "3px 10px", fontSize: 11, fontWeight: 600,
                                  }}>실패</span>
                                ) : sv === "saving" ? (
                                  <span style={{
                                    background: darkMode ? "#2a2a2a" : "#f3f4f6",
                                    color: darkMode ? "#888" : "#999",
                                    borderRadius: 99, padding: "3px 10px", fontSize: 11,
                                  }}>저장 중...</span>
                                ) : (
                                  <button
                                    onClick={() => saveRecipe(v.video_id, false)}
                                    style={{
                                      background: existingUrls.has(v.url) ? "none" : darkMode ? "#fff" : "#000",
                                      color: existingUrls.has(v.url) ? (darkMode ? "#aaa" : "#555") : darkMode ? "#000" : "#fff",
                                      border: existingUrls.has(v.url) ? `1px solid ${darkMode ? "#444" : "#ccc"}` : "none",
                                      borderRadius: 99, padding: "3px 10px", fontSize: 11, fontWeight: 500,
                                      cursor: "pointer",
                                    }}
                                  >
                                    {existingUrls.has(v.url) ? "덮어쓰기" : "저장"}
                                  </button>
                                )
                              ) : existingUrls.has(v.url) ? (
                                <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                                  <span style={{
                                    background: darkMode ? "#2a2a2a" : "#f3f4f6",
                                    color: darkMode ? "#aaa" : "#666",
                                    borderRadius: 99, padding: "3px 8px", fontSize: 11, fontWeight: 500,
                                  }}>저장됨</span>
                                  <button
                                    onClick={() => deleteRecipeByUrl(v.video_id, v.url)}
                                    style={{
                                      background: "none", color: darkMode ? "#666" : "#999",
                                      border: `1px solid ${darkMode ? "#444" : "#ddd"}`, borderRadius: 99,
                                      padding: "3px 8px", fontSize: 11, cursor: "pointer",
                                    }}
                                  >삭제</button>
                                </div>
                              ) : (
                                st === "failed" ? (
                                  <button
                                    onClick={() => reanalyzeVideo(v)}
                                    style={{
                                      background: "#eff6ff", color: "#2563eb",
                                      border: "1px solid #93c5fd", borderRadius: 99,
                                      padding: "3px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer",
                                    }}
                                  >다시 분석</button>
                                ) : "-"
                              )}
                            </td>
                          </tr>

                          {/* 분석 결과 — 완료/실패 시 항상 표시 */}
                          {(st === "done" || st === "failed") && res && (
                            <tr>
                              <td
                                colSpan={7}
                                style={{
                                  padding: "10px 16px 14px",
                                  background: darkMode ? "#111122" : "#f8faff",
                                  borderBottom: `1px solid ${darkMode ? "#333" : "#ddd"}`,
                                  fontSize: 13,
                                  wordBreak: "break-word",
                                  whiteSpace: "normal",
                                }}
                              >
                                {res.error ? (
                                  <div style={{ color: darkMode ? "#888" : "#777", borderLeft: `2px solid ${darkMode ? "#444" : "#ddd"}`, paddingLeft: 10 }}>{res.error}</div>
                                ) : editingId === v.video_id ? (
                                  /* ===== 수정 모드 ===== */
                                  <div>
                                    <div style={{ marginBottom: 6 }}>
                                      <b>메뉴:</b>
                                      <input
                                        value={editData.name}
                                        onChange={(e) => setEditData((d) => ({ ...d, name: e.target.value }))}
                                        style={{
                                          marginLeft: 8, padding: "3px 8px", borderRadius: 4, width: "60%",
                                          border: `1px solid ${darkMode ? "#444" : "#ccc"}`,
                                          background: darkMode ? "#222" : "#fff",
                                          color: darkMode ? "#eee" : "#111",
                                        }}
                                      />
                                    </div>
                                    <div style={{ marginBottom: 6 }}>
                                      <b>재료</b> <span style={{ fontSize: 11, color: "#999" }}>(쉼표로 구분)</span>
                                      <textarea
                                        value={editData.ingredients}
                                        onChange={(e) => setEditData((d) => ({ ...d, ingredients: e.target.value }))}
                                        rows={2}
                                        style={{
                                          display: "block", marginTop: 4, padding: 6, borderRadius: 4, width: "100%",
                                          border: `1px solid ${darkMode ? "#444" : "#ccc"}`,
                                          background: darkMode ? "#222" : "#fff",
                                          color: darkMode ? "#eee" : "#111", fontSize: 13,
                                        }}
                                      />
                                    </div>
                                    <div style={{ marginBottom: 6 }}>
                                      <b>요리 순서</b> <span style={{ fontSize: 11, color: "#999" }}>(한 줄에 한 단계)</span>
                                      <textarea
                                        value={editData.steps}
                                        onChange={(e) => setEditData((d) => ({ ...d, steps: e.target.value }))}
                                        rows={5}
                                        style={{
                                          display: "block", marginTop: 4, padding: 6, borderRadius: 4, width: "100%",
                                          border: `1px solid ${darkMode ? "#444" : "#ccc"}`,
                                          background: darkMode ? "#222" : "#fff",
                                          color: darkMode ? "#eee" : "#111", fontSize: 13,
                                        }}
                                      />
                                    </div>
                                    <div style={{ display: "flex", gap: 6 }}>
                                      <button
                                        onClick={() => saveEdit(v.video_id)}
                                        style={{
                                          background: "#16a34a", color: "#fff", border: "none",
                                          borderRadius: 6, padding: "4px 14px", fontSize: 13, cursor: "pointer",
                                        }}
                                      >저장</button>
                                      <button
                                        onClick={() => setEditingId(null)}
                                        style={{
                                          background: darkMode ? "#333" : "#e5e7eb", color: darkMode ? "#ccc" : "#555",
                                          border: "none", borderRadius: 6, padding: "4px 14px", fontSize: 13, cursor: "pointer",
                                        }}
                                      >취소</button>
                                    </div>
                                  </div>
                                ) : (
                                  /* ===== 보기 모드 ===== */
                                  <div>
                                    {/* 액션 버튼 */}
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                                      <div style={{ fontWeight: 600, fontSize: 14 }}>
                                        {sv === "duplicate" ? "🔄 이미 저장된 영상이에요 — 비교 후 결정해주세요" : res.name}
                                      </div>
                                      <div style={{ display: "flex", gap: 6 }}>
                                        {sv === "duplicate" && (
                                          <button
                                            onClick={() => saveRecipe(v.video_id, false)}
                                            style={{
                                              background: "none", color: darkMode ? "#aaa" : "#555",
                                              border: `1px solid ${darkMode ? "#444" : "#ccc"}`, borderRadius: 5,
                                              padding: "2px 10px", fontSize: 12, cursor: "pointer",
                                            }}
                                          >새 데이터로 덮어쓰기</button>
                                        )}
                                        <button
                                          onClick={() => startEdit(v.video_id)}
                                          style={{
                                            background: darkMode ? "#333" : "#e5e7eb", color: darkMode ? "#ccc" : "#555",
                                            border: "none", borderRadius: 5, padding: "2px 10px", fontSize: 12, cursor: "pointer",
                                          }}
                                        >수정</button>
                                        {(sv === "saved" || sv === "overwritten" || (existingUrls.has(res.video_url) && sv !== "deleted")) && (
                                          <button
                                            onClick={() => deleteRecipe(v.video_id)}
                                            style={{
                                              background: "none", color: darkMode ? "#777" : "#999",
                                              border: `1px solid ${darkMode ? "#444" : "#ddd"}`, borderRadius: 5, padding: "2px 10px", fontSize: 12, cursor: "pointer",
                                            }}
                                          >저장 취소</button>
                                        )}
                                      </div>
                                    </div>

                                    {/* 중복 시: 2열 비교 */}
                                    {sv === "duplicate" && channelDuplicateData[v.video_id] ? (() => {
                                      const ex = channelDuplicateData[v.video_id];
                                      const exIng = ex["재료"] || ex.ingredients || [];
                                      const exSteps = ex["순서"] || ex.steps || [];
                                      const cardBase = {
                                        borderRadius: 8, padding: "10px 12px", fontSize: 12,
                                        background: darkMode ? "#1a1a1a" : "#f9f9f9",
                                        border: `1px solid ${darkMode ? "#2a2a2a" : "#e5e7eb"}`,
                                        color: darkMode ? "#d4d4d4" : "#333",
                                      };
                                      return (
                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                                          {/* 기존 */}
                                          <div style={{ ...cardBase, borderLeft: "3px solid #f59e0b" }}>
                                            <div style={{ fontWeight: 700, fontSize: 11, marginBottom: 6, color: "#f59e0b", letterSpacing: 0.5 }}>
                                              💾 기존 저장 데이터
                                            </div>
                                            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>{ex["메뉴"] || ex.name}</div>
                                            <div style={{ marginBottom: 4 }}>
                                              <b>재료 ({exIng.length}):</b> {exIng.join(", ")}
                                            </div>
                                            {exSteps.length > 0 && (
                                              <div>
                                                <b>요리 순서:</b>
                                                <ol style={{ marginLeft: 14, marginTop: 3 }}>
                                                  {exSteps.map((s, i) => <li key={i} style={{ marginBottom: 2 }}>{s}</li>)}
                                                </ol>
                                              </div>
                                            )}
                                          </div>
                                          {/* 새 */}
                                          <div style={{ ...cardBase, borderLeft: "3px solid #22c55e" }}>
                                            <div style={{ fontWeight: 700, fontSize: 11, marginBottom: 6, color: "#22c55e", letterSpacing: 0.5 }}>
                                              🆕 새로 분석된 데이터
                                            </div>
                                            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>{res.name}</div>
                                            <div style={{ marginBottom: 4 }}>
                                              <b>재료 ({(res.ingredients || []).length}):</b> {(res.ingredients || []).join(", ")}
                                            </div>
                                            {res.steps && res.steps.length > 0 && (
                                              <div>
                                                <b>요리 순서:</b>
                                                <ol style={{ marginLeft: 14, marginTop: 3 }}>
                                                  {res.steps.map((s, i) => <li key={i} style={{ marginBottom: 2 }}>{s}</li>)}
                                                </ol>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })() : (
                                      /* 일반 단일 보기 */
                                      <>
                                        {/* 태그 뱃지 */}
                                        {(res.servings || (res.tags || []).length > 0) && (
                                          <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 6 }}>
                                            {res.servings && (
                                              <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, background: "#fff3e0", color: "#e07b00", border: "1px solid #fcd9a0", fontWeight: 500 }}>
                                                {res.servings}
                                              </span>
                                            )}
                                            {(res.tags || []).filter(t => t !== res.servings).map((tag, i) => (
                                              <span key={i} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, background: darkMode ? "#2a2a2a" : "#f0f0f0", color: darkMode ? "#bbb" : "#555", border: `1px solid ${darkMode ? "#3a3a3a" : "#e0e0e0"}` }}>
                                                {tag}
                                              </span>
                                            ))}
                                          </div>
                                        )}
                                        {/* Validator 결과 */}
                                        {res.validation_result && (
                                          <div style={{ fontSize: 11, marginBottom: 6, padding: "3px 8px", borderRadius: 6, background: res.validation_result.flagged?.length > 0 ? (darkMode ? "#2a1f00" : "#fffbe6") : (darkMode ? "#0d1f0d" : "#f0faf0"), color: res.validation_result.flagged?.length > 0 ? "#b07800" : "#2e7d32", border: `1px solid ${res.validation_result.flagged?.length > 0 ? "#fcd9a0" : "#a5d6a7"}` }}>
                                            {res.validation_result.summary}
                                            {res.validation_result.fixed?.length > 0 && (
                                              <span style={{ marginLeft: 6, opacity: 0.8 }}>
                                                ({res.validation_result.fixed.map(f => `${f.ingredient}: ${f.original}→${f.fixed}`).join(", ")})
                                              </span>
                                            )}
                                          </div>
                                        )}
                                        <div style={{ marginTop: 0 }}>
                                          <b>재료 ({(res.ingredients || []).length}):</b>{" "}
                                          <span style={{ color: darkMode ? "#888" : "#999", fontSize: 12 }}>
                                            (출처: {res.ingredients_source || res.source})
                                          </span>
                                          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
                                            {(res.ingredients || []).map((ing, i) => {
                                              const measured = (res.ingredients_measured || [])[i] || "";
                                              const amount = measured.startsWith(ing) ? measured.slice(ing.length).trim() : measured;
                                              return (
                                                <span key={i} style={{ fontSize: 12, padding: "2px 8px", borderRadius: 99, background: darkMode ? "#1e1e1e" : "#f5f5f5", border: `1px solid ${darkMode ? "#333" : "#e5e7eb"}`, color: darkMode ? "#ddd" : "#333" }}>
                                                  {ing}{amount && <span style={{ opacity: 0.6, marginLeft: 4 }}>{amount}</span>}
                                                </span>
                                              );
                                            })}
                                          </div>
                                        </div>
                                        {res.steps && res.steps.length > 0 && (
                                          <div style={{ marginTop: 8 }}>
                                            <b>요리 순서:</b>{" "}
                                            <span style={{ color: darkMode ? "#888" : "#999", fontSize: 12 }}>
                                              (출처: {res.steps_source || res.source})
                                            </span>
                                            <ol style={{ marginLeft: 20, marginTop: 2 }}>
                                              {res.steps.map((s, j) => (
                                                <li key={j} style={{ marginBottom: 2 }}>{s}</li>
                                              ))}
                                            </ol>
                                          </div>
                                        )}
                                        {(res.tips || []).length > 0 && (
                                          <div style={{ marginTop: 8, fontSize: 13 }}>
                                            <b>💡 TIP</b>
                                            <ul style={{ marginLeft: 16, marginTop: 4 }}>
                                              {res.tips.map((t, i) => (
                                                <li key={i} style={{ marginBottom: 2, color: darkMode ? "#bbb" : "#555" }}>{t}</li>
                                              ))}
                                            </ul>
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            );
          })()}
        </>
      )}

      {/* ===== 최근 분석 탭 ===== */}
      {tab === "recent" && (
        <>
          {!recentResults || !recentResults.items?.length ? (
            <div style={{ padding: "24px 0", color: darkMode ? "#666" : "#bbb", textAlign: "center", fontSize: 14 }}>
              아직 분석한 영상이 없어요.<br />
              <span style={{ fontSize: 12 }}>영상 분석 또는 채널 분석 탭에서 분석해보세요.</span>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 12, color: darkMode ? "#555" : "#bbb", marginBottom: 14 }}>
                마지막 분석 세션 · 영상 {recentResults.items.length}개
              </div>
              {recentResults.items.map((item, itemIdx) => (
                item.results.map((recipe, i) => {
                  const recentKey = `recent_${itemIdx}_${i}`;
                  const sv = recipeSaveStatus[recentKey] || "";
                  return (
                  <div key={`${itemIdx}-${i}`} style={{
                    padding: "14px 16px",
                    marginBottom: 10,
                    borderRadius: 10,
                    background: darkMode ? "#141414" : "#fff",
                    border: `1px solid ${darkMode ? "#252525" : "#e8e8e8"}`,
                    color: darkMode ? "#e0e0e0" : "#222",
                  }}>
                    {/* 헤더: 제목 + 버튼들 */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 15 }}>{recipe.name}</div>
                        <div style={{ fontSize: 12, color: darkMode ? "#666" : "#aaa", marginTop: 2 }}>
                          {recipe.uploader} · {recipe.upload_date}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0, marginLeft: 12 }}>
                        {sv === "saved" || sv === "overwritten" ? (
                          <span style={{ fontSize: 12, color: darkMode ? "#aaa" : "#666" }}>
                            {sv === "overwritten" ? "갱신됨 ✓" : "저장됨 ✓"}
                          </span>
                        ) : sv === "saving" ? (
                          <span style={{ fontSize: 12, color: darkMode ? "#888" : "#999" }}>저장 중...</span>
                        ) : sv === "error" ? (
                          <span style={{ fontSize: 12, color: "#dc2626" }}>실패</span>
                        ) : (
                          <button
                            onClick={async () => {
                              setRecipeSaveStatus(prev => ({ ...prev, [recentKey]: "saving" }));
                              try {
                                const result = await requestSave({ result: recipe });
                                if (result.saved) {
                                  setRecipeSaveStatus(prev => ({ ...prev, [recentKey]: "saved" }));
                                  onSaveSuccess?.();
                                } else if (result.duplicate) {
                                  setRecipeSaveStatus(prev => ({ ...prev, [recentKey]: "duplicate" }));
                                  openDupModal(recipe, recentKey, result.existingList, (finalStatus) => {
                                    setRecipeSaveStatus(prev => ({ ...prev, [recentKey]: finalStatus }));
                                    if (finalStatus === "saved" || finalStatus === "overwritten") onSaveSuccess?.();
                                  });
                                } else {
                                  setRecipeSaveStatus(prev => ({ ...prev, [recentKey]: "error" }));
                                }
                              } catch {
                                setRecipeSaveStatus(prev => ({ ...prev, [recentKey]: "error" }));
                              }
                            }}
                            style={{
                              background: darkMode ? "#fff" : "#000", color: darkMode ? "#000" : "#fff",
                              border: "none", borderRadius: 6, padding: "4px 12px",
                              fontSize: 12, fontWeight: 600, cursor: "pointer",
                            }}
                          >저장</button>
                        )}
                        <a href={item.url} target="_blank" rel="noreferrer" style={{
                          fontSize: 11, color: darkMode ? "#666" : "#aaa",
                          textDecoration: "none",
                          border: `1px solid ${darkMode ? "#333" : "#ddd"}`,
                          borderRadius: 4, padding: "2px 7px",
                        }}>↗ YouTube</a>
                      </div>
                    </div>
                    {/* 태그 */}
                    {(recipe.servings || (recipe.tags || []).length > 0) && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 8 }}>
                        {recipe.servings && (
                          <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, background: "#fff3e0", color: "#e07b00", border: "1px solid #fcd9a0", fontWeight: 500 }}>
                            {recipe.servings}
                          </span>
                        )}
                        {(recipe.tags || []).filter(t => t !== recipe.servings).map((tag, i) => (
                          <span key={i} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, background: darkMode ? "#2a2a2a" : "#f0f0f0", color: darkMode ? "#bbb" : "#555", border: `1px solid ${darkMode ? "#3a3a3a" : "#e0e0e0"}` }}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    {/* 재료 + 용량 */}
                    <div style={{ fontSize: 13, marginBottom: 6 }}>
                      <b>재료 ({(recipe.ingredients || []).length}):</b>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
                        {(recipe.ingredients || []).map((ing, i) => {
                          const measured = (recipe.ingredients_measured || [])[i] || "";
                          const amount = measured.startsWith(ing) ? measured.slice(ing.length).trim() : measured;
                          return (
                            <span key={i} style={{ fontSize: 12, padding: "2px 8px", borderRadius: 99, background: darkMode ? "#1e1e1e" : "#f5f5f5", border: `1px solid ${darkMode ? "#333" : "#e5e7eb"}`, color: darkMode ? "#ddd" : "#333" }}>
                              {ing}{amount && <span style={{ opacity: 0.6, marginLeft: 4 }}>{amount}</span>}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                    {/* 순서 */}
                    {recipe.steps && recipe.steps.length > 0 && (
                      <div style={{ fontSize: 13, marginBottom: 6 }}>
                        <b>요리 순서:</b>
                        <ol style={{ marginLeft: 18, marginTop: 4 }}>
                          {recipe.steps.map((s, j) => <li key={j} style={{ marginBottom: 2 }}>{s}</li>)}
                        </ol>
                      </div>
                    )}
                    {/* TIP */}
                    {(recipe.tips || []).length > 0 && (
                      <div style={{ fontSize: 13 }}>
                        <b>💡 TIP</b>
                        <ul style={{ marginLeft: 16, marginTop: 4 }}>
                          {recipe.tips.map((t, i) => (
                            <li key={i} style={{ marginBottom: 2, color: darkMode ? "#bbb" : "#555" }}>{t}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  );
                })
              ))}
            </div>
          )}
        </>
      )}

      {/* ===== 중복 선택 모달 ===== */}
      {dupModal && (
        <div
          onClick={dupBusy ? undefined : closeDupModal}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 9999, padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: darkMode ? "#1a1a1a" : "#fff",
              color: darkMode ? "#e0e0e0" : "#222",
              borderRadius: 12,
              padding: 20,
              maxWidth: 460,
              width: "100%",
              maxHeight: "80vh",
              overflow: "auto",
              border: `1px solid ${darkMode ? "#333" : "#e0e0e0"}`,
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            }}
          >
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>
              이 영상에 이미 저장된 레시피가 있어요
            </div>
            <div style={{ fontSize: 12, color: darkMode ? "#888" : "#777", marginBottom: 14 }}>
              같은 요리면 <b>업데이트</b>, 다른 요리면 <b>새 레시피로 추가</b>하세요.
            </div>

            <div style={{ marginBottom: 12, fontSize: 12, color: darkMode ? "#aaa" : "#666" }}>
              분석 결과: <b style={{ color: darkMode ? "#fff" : "#000" }}>{dupModal.recipe?.name || "-"}</b>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
              {(dupModal.existingList || []).map((row) => (
                <button
                  key={row.id}
                  disabled={dupBusy}
                  onClick={async () => {
                    setDupBusy(true);
                    try {
                      const result = await requestSave({
                        result: dupModal.recipe,
                        target_id: row.id,
                      });
                      const status = result.saved ? "overwritten" : "error";
                      dupModal.onDone?.(status);
                    } catch {
                      dupModal.onDone?.("error");
                    }
                    closeDupModal();
                  }}
                  style={{
                    background: darkMode ? "#252525" : "#f7f7f7",
                    color: darkMode ? "#e0e0e0" : "#222",
                    border: `1px solid ${darkMode ? "#3a3a3a" : "#e0e0e0"}`,
                    borderRadius: 8,
                    padding: "10px 12px",
                    textAlign: "left",
                    cursor: dupBusy ? "wait" : "pointer",
                    fontSize: 13,
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 2 }}>
                    「{row.name}」 업데이트
                  </div>
                  <div style={{ fontSize: 11, color: darkMode ? "#888" : "#999", wordBreak: "break-all" }}>
                    {row.url}
                  </div>
                </button>
              ))}
            </div>

            <button
              disabled={dupBusy}
              onClick={async () => {
                setDupBusy(true);
                try {
                  const result = await requestSave({
                    result: dupModal.recipe,
                    save_as_new: true,
                  });
                  const status = result.saved ? "saved" : "error";
                  dupModal.onDone?.(status);
                } catch {
                  dupModal.onDone?.("error");
                }
                closeDupModal();
              }}
              style={{
                width: "100%",
                background: darkMode ? "#fff" : "#000",
                color: darkMode ? "#000" : "#fff",
                border: "none",
                borderRadius: 8,
                padding: "10px 12px",
                fontSize: 13,
                fontWeight: 600,
                cursor: dupBusy ? "wait" : "pointer",
                marginBottom: 8,
              }}
            >+ 새 레시피로 추가 (다른 요리인 경우)</button>

            <button
              disabled={dupBusy}
              onClick={closeDupModal}
              style={{
                width: "100%",
                background: "none",
                color: darkMode ? "#888" : "#666",
                border: `1px solid ${darkMode ? "#333" : "#ddd"}`,
                borderRadius: 8,
                padding: "8px 12px",
                fontSize: 12,
                cursor: dupBusy ? "wait" : "pointer",
              }}
            >취소</button>
          </div>
        </div>
      )}
    </section>
  );
}
